import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchBranches } from '../../api/salon';
import { fetchStaffDirectory, inviteStaff, linkStaff } from '../../api/staff';
import { useToast } from '../../context/ToastContext';
import { useDebounce } from '../../hooks/useDebounce';
import { getFriendlyError } from '../../utils/friendlyError';
import Input from '../ui/Input';
import SearchAutocomplete from '../ui/SearchAutocomplete';
import StaffBranchScheduleEditor, { toApiSchedules } from './StaffBranchScheduleEditor';
import StaffSalaryFields, { emptySalaryValues, salaryToApi } from './StaffSalaryFields';

const PERM_KEYS = [
  'canManageAppointments',
  'canManageOrders',
  'canViewAnalytics',
  'canManageProducts',
  'canManageInventory',
  'canManageStaff',
  'canManageSalaries',
  'canUsePos',
];

const defaultPermissions = {
  canManageAppointments: true,
  canManageOrders: false,
  canViewAnalytics: false,
  canManageProducts: false,
  canManageInventory: false,
  canManageStaff: false,
  canManageSalaries: false,
  canUsePos: true,
};

const PermissionChecklist = ({ permissions, onChange, t }) => (
  <div className="space-y-2">
    {PERM_KEYS.map((key) => (
      <label key={key} className="flex items-center justify-between rounded-lg border border-zinc-200 dark:border-zinc-800 px-3 py-2 text-sm">
        <span>{t(`staff.permissions.${key}`)}</span>
        <input
          type="checkbox"
          checked={permissions[key]}
          onChange={(e) => onChange((p) => ({ ...p, [key]: e.target.checked }))}
        />
      </label>
    ))}
  </div>
);

const AddStaffModal = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [mode, setMode] = useState('search');
  const [searchInput, setSearchInput] = useState('');
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [permissions, setPermissions] = useState(defaultPermissions);
  const [salary, setSalary] = useState(emptySalaryValues());
  const [branchSchedules, setBranchSchedules] = useState([]);
  const debouncedSearch = useDebounce(searchInput, 300);

  const { data: branchesData } = useQuery({
    queryKey: ['salon-branches'],
    queryFn: fetchBranches,
    enabled: open,
  });

  const { data: directoryData, isFetching: directoryLoading } = useQuery({
    queryKey: ['staff-directory', debouncedSearch],
    queryFn: () => fetchStaffDirectory({ q: debouncedSearch }),
    enabled: open && mode === 'search' && debouncedSearch.trim().length >= 1,
    staleTime: 20_000,
  });

  const reset = () => {
    setMode('search');
    setSearchInput('');
    setSelectedStaffId('');
    setSelectedLabel('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setPassword('');
    setPermissions(defaultPermissions);
    setSalary(emptySalaryValues());
    setBranchSchedules([]);
  };

  const linkMut = useMutation({
    mutationFn: () =>
      linkStaff({
        userId: selectedStaffId,
        permissions,
        branchSchedules: toApiSchedules(branchSchedules),
        ...salaryToApi(salary),
      }),
    onSuccess: (res) => {
      onSuccess?.(res);
      reset();
      onClose();
    },
    onError: (err) => showToast(getFriendlyError(err, t('toast.inviteFailed')), 'error'),
  });

  const inviteMut = useMutation({
    mutationFn: () =>
      inviteStaff({
        firstName,
        lastName,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        password: password.trim() || undefined,
        permissions,
        branchSchedules: toApiSchedules(branchSchedules),
        ...salaryToApi(salary),
      }),
    onSuccess: (res) => {
      onSuccess?.(res);
      reset();
      onClose();
    },
    onError: (err) => showToast(getFriendlyError(err, t('toast.inviteFailed')), 'error'),
  });

  if (!open) return null;

  const busy = linkMut.isPending || inviteMut.isPending;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
        <h2 className="text-lg font-semibold">{t('staff.add.title')}</h2>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-500">
          {mode === 'search' ? t('staff.add.subtitleSearch') : t('staff.add.subtitle')}
        </p>

        <div className="mt-4 flex rounded-lg border border-zinc-200 dark:border-zinc-800 p-1">
          <button
            type="button"
            onClick={() => setMode('search')}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm ${mode === 'search' ? 'bg-glow-600/20 text-glow-300' : 'text-zinc-600 dark:text-zinc-400'}`}
          >
            {t('staff.add.searchExisting')}
          </button>
          <button
            type="button"
            onClick={() => setMode('new')}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm ${mode === 'new' ? 'bg-glow-600/20 text-glow-300' : 'text-zinc-600 dark:text-zinc-400'}`}
          >
            {t('staff.add.createNew')}
          </button>
        </div>

        <div className="mt-4 space-y-4">
          {mode === 'search' ? (
            <>
              <SearchAutocomplete
                value={searchInput}
                onChange={(v) => {
                  setSearchInput(v);
                  if (!v.trim()) {
                    setSelectedStaffId('');
                    setSelectedLabel('');
                  }
                }}
                onSearch={(item) => {
                  if (item && typeof item === 'object') {
                    setSelectedStaffId(item.value);
                    setSelectedLabel(item.label);
                    setSearchInput(item.label);
                  }
                }}
                suggestions={directoryData?.suggestions || []}
                loading={directoryLoading}
                placeholder={t('staff.add.searchPlaceholder')}
              />
              {selectedStaffId && (
                <p className="text-sm text-emerald-600 dark:text-emerald-400">
                  {t('staff.add.selected', { name: selectedLabel })}
                </p>
              )}
            </>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input label={t('staff.invite.firstName')} value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
                <Input label={t('staff.invite.lastName')} value={lastName} onChange={(e) => setLastName(e.target.value)} required />
              </div>
              <Input label={t('staff.invite.email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <Input label={t('common.phone')} value={phone} onChange={(e) => setPhone(e.target.value)} />
              <Input
                label={t('staff.invite.initialPassword')}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('staff.invite.initialPasswordHint')}
              />
              <p className="text-xs text-zinc-500">{t('staff.invite.mustChangeOnFirstLogin')}</p>
            </>
          )}

          <div>
            <p className="mb-2 text-sm text-zinc-600 dark:text-zinc-400">{t('staff.invite.permissions')}</p>
            <PermissionChecklist permissions={permissions} onChange={setPermissions} t={t} />
          </div>

          <div>
            <p className="mb-2 text-sm text-zinc-600 dark:text-zinc-400">{t('staff.salary.setupTitle')}</p>
            <p className="mb-3 text-xs text-zinc-500">{t('staff.salary.setupHint')}</p>
            <StaffSalaryFields values={salary} onChange={setSalary} t={t} />
          </div>

          <StaffBranchScheduleEditor
            branches={branchesData?.branches || []}
            value={branchSchedules}
            onChange={setBranchSchedules}
          />
        </div>

        <div className="mt-6 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-zinc-300 dark:border-zinc-700 py-2 text-sm">
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={
              busy
              || (mode === 'search'
                ? !selectedStaffId
                : !firstName || !lastName || (!email.trim() && !phone.trim()) || (password && password.length < 8))
            }
            onClick={() => (mode === 'search' ? linkMut.mutate() : inviteMut.mutate())}
            className="flex-1 rounded-lg bg-glow-600 py-2 text-sm text-white disabled:opacity-50"
          >
            {busy ? t('common.saving') : mode === 'search' ? t('staff.add.sendInvitation') : t('staff.invite.sendInvite')}
          </button>
        </div>
      </div>
    </>
  );
};

export default AddStaffModal;
